import type { SemanticTone } from '@/theme/tokens';
import type {
  BackupJobStato,
  ClienteStato,
  ContrattoStato,
  EventSeverity,
  FirmaStato,
  Ruolo,
  ServizioEsito,
  StatoCedolino,
  StatoPreventivo,
  StatoPreventivoCliente,
  TicketPriorita,
  TicketStato,
  TicketFonte,
  TaskPriorita,
  TaskStato,
  TipoCedolinoVoce,
  LeadFonte,
  LeadStato,
} from '@/types';

/**
 * Maps domain enums to a semantic tone and an Italian label.
 *
 * Every status pill in the app resolves through here, which is what keeps
 * "aperto" the same colour on the dashboard, the ticket list and the admin
 * queue without any component hard-coding a palette entry.
 */
export interface StatusMeta {
  label: string;
  tone: SemanticTone;
}

const fallback: StatusMeta = { label: 'Sconosciuto', tone: 'neutral' };

export const clienteStatoMeta: Record<ClienteStato, StatusMeta> = {
  attivo: { label: 'Attivo', tone: 'success' },
  sospeso: { label: 'Sospeso', tone: 'warning' },
  cessato: { label: 'Cessato', tone: 'neutral' },
  in_attivazione: { label: 'In attivazione', tone: 'info' },
  perso: { label: 'In archivio', tone: 'error' },
};

export const contrattoStatoMeta: Record<ContrattoStato, StatusMeta> = {
  bozza: { label: 'Bozza', tone: 'neutral' },
  inviato: { label: 'Inviato', tone: 'info' },
  firmato: { label: 'Firmato', tone: 'info' },
  attivo: { label: 'Attivo', tone: 'success' },
  scaduto: { label: 'Scaduto', tone: 'warning' },
  cessato: { label: 'Cessato', tone: 'neutral' },
  cancellato: { label: 'Cancellato', tone: 'error' },
};

export const firmaStatoMeta: Record<FirmaStato, StatusMeta> = {
  non_inviato: { label: 'Non inviato', tone: 'neutral' },
  inviato: { label: 'Inviato al cliente', tone: 'info' },
  in_firma: { label: 'In firma', tone: 'warning' },
  firmato: { label: 'Firmato', tone: 'success' },
  rifiutato: { label: 'Rifiutato', tone: 'error' },
  scaduto: { label: 'Scaduto', tone: 'warning' },
};

export const documentoTipoMeta: Record<string, StatusMeta> = {
  contratto_da_firmare: { label: 'Da firmare', tone: 'info' },
  contratto_firmato: { label: 'Firmato', tone: 'success' },
  da_firmare: { label: 'Da firmare', tone: 'info' },
  firmato: { label: 'Firmato', tone: 'success' },
  allegato: { label: 'Allegato', tone: 'neutral' },
};

export const contrattoTipoMeta: Record<'base' | 'servizio', StatusMeta> = {
  base: { label: 'Accordo quadro', tone: 'info' },
  servizio: { label: 'Offerta', tone: 'neutral' },
};

export const contrattoFamigliaMeta: Record<
  'quadro' | 'canone' | 'una_tantum',
  { label: string; description: string; createLabel: string }
> = {
  quadro: {
    label: 'Accordo quadro',
    description: 'L’intesa di riferimento con il cliente. Non contiene voci di listino.',
    createLabel: 'Nuovo accordo',
  },
  canone: {
    label: 'Canoni',
    description: 'Servizi che si rinnovano, con un canone periodico.',
    createLabel: 'Nuovo canone',
  },
  una_tantum: {
    label: 'Una tantum',
    description: 'Prodotti e licenze saldati in un’unica soluzione.',
    createLabel: 'Nuova vendita',
  },
};

export function isDocumentoDaFirmare(tipo: string, firmato?: boolean): boolean {
  if (firmato) return false;
  return tipo === 'contratto_da_firmare' || tipo === 'da_firmare';
}

export function isDocumentoFirmato(tipo: string, firmato?: boolean): boolean {
  return tipo === 'contratto_firmato' || tipo === 'firmato' || Boolean(firmato);
}

/** The customer still needs to sign this header. */
export function contrattoDaFirmare(contratto: { firma_stato?: string | null } | null | undefined): boolean {
  const stato = contratto?.firma_stato ?? 'non_inviato';
  return stato !== 'firmato';
}

export const FIRMA_STEPPER_STEPS: FirmaStato[] = ['non_inviato', 'inviato', 'in_firma', 'firmato'];

export function firmaStepperIndex(stato: FirmaStato | string | undefined): number {
  if (stato === 'firmato') return 4;
  if (stato === 'in_firma') return 2;
  if (stato === 'inviato') return 1;
  if (stato === 'rifiutato' || stato === 'scaduto') return 2;
  return 0;
}

export const rigaStatoMeta: Record<string, StatusMeta> = {
  attivo: { label: 'Attiva', tone: 'success' },
  sospeso: { label: 'Sospesa', tone: 'warning' },
  scaduto: { label: 'Scaduta', tone: 'warning' },
  cancellato: { label: 'Cancellata', tone: 'error' },
};

export const contrattoGestitoMeta: Record<'gestito' | 'non_gestito', StatusMeta> = {
  /** Blue, not teal: `success` is already used by stato=attivo. */
  gestito: { label: 'Gestito', tone: 'info' },
  non_gestito: { label: 'Non gestito', tone: 'warning' },
};

export const contrattoGestitoTooltip: Record<'gestito' | 'non_gestito', string> = {
  gestito: 'Sotto gestione: i moduli di monitoraggio sono attivi nell’hub.',
  non_gestito: 'Venduto senza gestione: resta in anagrafica e non abilita i moduli di monitoraggio.',
};

export function contrattoGestitoStatus(gestito: boolean): StatusMeta {
  return gestito ? contrattoGestitoMeta.gestito : contrattoGestitoMeta.non_gestito;
}

export const leadStatoMeta: Record<LeadStato, StatusMeta> = {
  nuovo: { label: 'Nuovo', tone: 'info' },
  contattato: { label: 'Contattato', tone: 'warning' },
  da_richiamare: { label: 'Da richiamare', tone: 'warning' },
  appuntamento_fissato: { label: 'Appuntamento fissato', tone: 'info' },
  qualificato: { label: 'Qualificato', tone: 'success' },
  convertito: { label: 'Convertito', tone: 'success' },
  perso: { label: 'Perso', tone: 'error' },
};

export const leadFonteMeta: Record<LeadFonte, StatusMeta> = {
  manuale: { label: 'Manuale', tone: 'neutral' },
  import_excel: { label: 'Excel', tone: 'info' },
  form_pubblico: { label: 'Form pubblico', tone: 'info' },
  preventivo: { label: 'Preventivo', tone: 'neutral' },
  email: { label: 'Email', tone: 'neutral' },
  meta_ads: { label: 'Meta Ads', tone: 'info' },
  google_ads: { label: 'Google Ads', tone: 'info' },
  altro: { label: 'Altro', tone: 'neutral' },
};

export const ticketStatoMeta: Record<TicketStato, StatusMeta> = {
  aperto: { label: 'Aperto', tone: 'info' },
  in_lavorazione: { label: 'In lavorazione', tone: 'warning' },
  risolto: { label: 'Risolto', tone: 'success' },
  chiuso: { label: 'Chiuso', tone: 'neutral' },
};

export const ticketPrioritaMeta: Record<TicketPriorita, StatusMeta> = {
  bassa: { label: 'Bassa', tone: 'neutral' },
  media: { label: 'Media', tone: 'info' },
  alta: { label: 'Alta', tone: 'warning' },
  critica: { label: 'Critica', tone: 'critical' },
};

export const ticketFonteMeta: Record<TicketFonte, StatusMeta> = {
  app: { label: 'App', tone: 'info' },
  email: { label: 'Email', tone: 'neutral' },
  telefono: { label: 'Contatto telefonico', tone: 'success' },
  alert: { label: 'Generato da alert', tone: 'warning' },
};

export const taskStatoMeta: Record<TaskStato, StatusMeta> = {
  da_fare: { label: 'Da fare', tone: 'neutral' },
  in_corso: { label: 'In corso', tone: 'info' },
  in_pausa: { label: 'In pausa', tone: 'warning' },
  completato: { label: 'Completato', tone: 'success' },
  annullato: { label: 'Annullato', tone: 'neutral' },
};

export const taskPrioritaMeta: Record<TaskPriorita, StatusMeta> = {
  bassa: { label: 'Bassa', tone: 'neutral' },
  media: { label: 'Media', tone: 'info' },
  alta: { label: 'Alta', tone: 'warning' },
  critica: { label: 'Critica', tone: 'critical' },
};

export const severityMeta: Record<EventSeverity, StatusMeta> = {
  info: { label: 'Info', tone: 'info' },
  warning: { label: 'Warning', tone: 'warning' },
  error: { label: 'Errore', tone: 'error' },
  critical: { label: 'Critico', tone: 'critical' },
};

export const ruoloMeta: Record<Ruolo, StatusMeta> = {
  admin: { label: 'Amministratore', tone: 'critical' },
  operatore: { label: 'Operatore', tone: 'info' },
  operatore_amministrativo: { label: 'Operatore amministrativo', tone: 'warning' },
  cliente_admin: { label: 'Admin cliente', tone: 'success' },
  cliente_user: { label: 'Utente', tone: 'neutral' },
};

export function ruoloStatus(ruolo: Ruolo | string | null | undefined): StatusMeta {
  if (ruolo && ruolo in ruoloMeta) return ruoloMeta[ruolo as Ruolo]!;
  return { label: ruolo ?? 'Ruolo', tone: 'neutral' };
}

export const cedolinoStatoMeta: Record<StatoCedolino, StatusMeta> = {
  in_attesa: { label: 'In attesa', tone: 'warning' },
  bozza: { label: 'Bozza', tone: 'neutral' },
  in_approvazione: { label: 'In approvazione', tone: 'warning' },
  approvato: { label: 'Approvato', tone: 'info' },
  inviato: { label: 'Inviato', tone: 'info' },
  pagato: { label: 'Pagato', tone: 'success' },
  annullato: { label: 'Annullato', tone: 'error' },
};

export const preventivoStatoMeta: Record<StatoPreventivo, StatusMeta> = {
  bozza: { label: 'Bozza', tone: 'neutral' },
  inviato: { label: 'Inviato', tone: 'info' },
  accettato: { label: 'Accettato', tone: 'success' },
  rifiutato: { label: 'Rifiutato', tone: 'error' },
  scaduto: { label: 'Scaduto', tone: 'warning' },
  convertito: { label: 'Convertito', tone: 'success' },
};

export const preventivoStatoClienteMeta: Record<StatoPreventivoCliente, StatusMeta> = {
  da_inviare: { label: 'Da inviare', tone: 'warning' },
  inviato: { label: 'Inviato', tone: 'info' },
  accettato: { label: 'Accettato', tone: 'success' },
  contestato: { label: 'Contestato', tone: 'error' },
  fatturato: { label: 'Fatturato', tone: 'success' },
  annullato: { label: 'Annullato', tone: 'neutral' },
};

export const cedolinoVoceTipoMeta: Record<TipoCedolinoVoce, StatusMeta> = {
  tempo_risoluzione: { label: 'Ticket', tone: 'info' },
  carburante: { label: 'Carburante', tone: 'warning' },
  chilometraggio: { label: 'Km', tone: 'info' },
  pernottamento: { label: 'Pernottamento', tone: 'neutral' },
  altro: { label: 'Altro', tone: 'neutral' },
};

/**
 * `backup_jobs.job_status` and `macchina_servizio_stato.ultimo_esito` are free
 * VARCHARs fed by heterogeneous vendor e-mails, so these two matchers normalise
 * loosely (substring, case-insensitive) instead of using an exhaustive record.
 */
export function backupStatoMeta(status: BackupJobStato | null | undefined): StatusMeta {
  if (!status) return fallback;
  const value = status.toLowerCase();
  if (value.includes('success') || value === 'ok' || value.includes('completed')) {
    return { label: 'Completato', tone: 'success' };
  }
  if (value.includes('critical') || value.includes('critic')) return { label: 'Critico', tone: 'critical' };
  if (value.includes('warn')) return { label: 'Warning', tone: 'warning' };
  if (value.includes('fail') || value.includes('error')) return { label: 'Fallito', tone: 'error' };
  if (value.includes('run') || value.includes('progress')) return { label: 'In corso', tone: 'info' };
  if (value.includes('cancel') || value.includes('abort')) return { label: 'Annullato', tone: 'neutral' };
  return { label: status, tone: 'neutral' };
}

export function servizioEsitoMeta(esito: ServizioEsito | null | undefined): StatusMeta {
  if (!esito) return { label: 'Nessun dato', tone: 'neutral' };
  const value = esito.toLowerCase();
  if (value === 'ok' || value.includes('success') || value.includes('health')) {
    return { label: 'Operativo', tone: 'success' };
  }
  if (value === 'unknown' || value === 'n/a' || value === 'pending') {
    return { label: 'In attesa', tone: 'info' };
  }
  if (value.includes('warn') || value.includes('degrad')) return { label: 'Degradato', tone: 'warning' };
  if (value.includes('error') || value.includes('fail') || value.includes('down')) {
    return { label: 'In errore', tone: 'error' };
  }
  if (value.includes('critical')) return { label: 'Critico', tone: 'critical' };
  return { label: esito, tone: 'neutral' };
}

/** Italian label for a `servizi.modulo` value. */
export const moduloLabels: Record<string, string> = {
  backup: 'Backup',
  firewall: 'Firewall',
  antivirus: 'Antivirus',
  syslog: 'Syslog',
};

export function moduloLabel(modulo: string | null | undefined): string {
  if (!modulo) return 'Servizio';
  return moduloLabels[modulo.toLowerCase()] ?? modulo;
}

export function rigaCatalogName(riga: {
  servizio?: { nome?: string | null } | null;
  prodotto?: { nome?: string | null } | null;
} | null | undefined): string {
  return riga?.servizio?.nome || riga?.prodotto?.nome || '—';
}

/** Compact label for a contract header: catalog names, then numero. */
export function contrattoTitolo(contratto: {
  titolo?: string | null;
  numero_contratto?: string | null;
  righe?: Array<{ servizio?: { nome?: string | null } | null; prodotto?: { nome?: string | null } | null }>;
} | null | undefined): string {
  if (!contratto) return 'Contratto';
  if (contratto.titolo) return contratto.titolo;
  const names = (contratto.righe ?? [])
    .map(rigaCatalogName)
    .filter((name) => name !== '—');
  return names.join(', ') || contratto.numero_contratto || 'Contratto';
}

export function rigaModulo(riga: { servizio?: { modulo?: string | null } | null } | null | undefined): string | null {
  return riga?.servizio?.modulo ?? null;
}

export function isBackupRiga(riga: { servizio?: { modulo?: string | null } | null } | null | undefined): boolean {
  return rigaModulo(riga)?.toLowerCase() === 'backup';
}

/** True when the machine is attached to a managed backup line, or already has backup health. */
export function macchinaHasBackupMonitoring(input: {
  righe?: Array<{ servizio?: { modulo?: string | null } | null }> | null;
  servizi?: Array<{ contratto?: { prodotto?: { modulo?: string | null } | null } | null }> | null;
}): boolean {
  if ((input.righe ?? []).some(isBackupRiga)) return true;
  return (input.servizi ?? []).some((stato) => stato.contratto?.prodotto?.modulo?.toLowerCase() === 'backup');
}
